'use client';

/**
 * CreateProjectWizard Component
 *
 * A multi-step wizard for creating new projects in the admin interface.
 *
 * Steps:
 * 1. Organization Selection - Choose the organization for the project
 * 2. Connection Selection - Pick existing GitHub connection from that org
 * 3. Repository Selection - Browse and select from available repositories
 * 4. Project Details - Configure project name, slug, description (org pre-filled)
 *
 * @see Issue: Admin Interface - Projects Management
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Center,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Stack,
  Text,
  TextArea,
  WizardStep,
  type WizardStepItem,
} from '@cortex/ui';
import {
  AlertCircle,
  Check,
  Github,
  Globe,
  Lock,
  Search,
  Star,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import { ConnectionHealthBadge } from './ConnectionHealthBadge';

// =============================================================================
// Types
// =============================================================================

interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface OrgConnection {
  id: string;
  label: string;
  provider_id: string;
  provider_type: string;
  metadata: {
    github_account?: {
      login: string;
      avatar_url: string;
      type: 'User' | 'Organization';
    };
    repository_selection?: 'all' | 'selected';
  };
  validation_status: string | null;
}

interface GitHubRepo {
  id: number;
  name: string;
  fullName: string;
  description: string | null;
  url: string;
  cloneUrl: string;
  sshUrl: string;
  isPrivate: boolean;
  owner: {
    login: string;
    avatarUrl: string;
  };
  defaultBranch: string;
  language: string | null;
  stars: number;
  updatedAt: string;
  pushedAt: string;
}

interface CreateProjectWizardProps {
  /** Whether the wizard modal is open */
  open: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Available organizations for the project */
  organizations: Organization[];
  /** Callback when a project is successfully created */
  onSuccess: () => void;
  /** Pre-selected organization ID (skips org selection step) */
  preselectedOrganizationId?: string;
}

// =============================================================================
// Constants
// =============================================================================

const WIZARD_STEPS: WizardStepItem[] = [
  {
    id: 'organization',
    title: 'Select Organization',
    description: 'Choose the organization for this project',
  },
  {
    id: 'connection',
    title: 'Select Connection',
    description: 'Choose a GitHub connection to use',
  },
  {
    id: 'repository',
    title: 'Select Repository',
    description: 'Choose the repository for this project',
  },
  {
    id: 'details',
    title: 'Project Details',
    description: 'Configure project settings',
  },
];

// =============================================================================
// Component
// =============================================================================

export function CreateProjectWizard({
  open,
  onClose,
  organizations,
  onSuccess,
  preselectedOrganizationId,
}: CreateProjectWizardProps) {
  // ---------------------------------------------------------------------------
  // Wizard State
  // ---------------------------------------------------------------------------
  const fetchWithAuth = useFetchWithAuth();

  // When org is preselected, skip step 0 (org selection)
  const hasPreselectedOrg = !!preselectedOrganizationId;
  const initialStep = hasPreselectedOrg ? 1 : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);

  // Filter wizard steps when org is preselected
  const wizardSteps = useMemo(() => {
    if (hasPreselectedOrg) {
      return WIZARD_STEPS.filter((step) => step.id !== 'organization');
    }
    return WIZARD_STEPS;
  }, [hasPreselectedOrg]);

  // Map display step index to actual step index
  const displayStepIndex = hasPreselectedOrg ? currentStep - 1 : currentStep;

  // ---------------------------------------------------------------------------
  // Step 1: Organization Selection State
  // ---------------------------------------------------------------------------
  const [selectedOrgId, setSelectedOrgId] = useState(
    preselectedOrganizationId || ''
  );

  // ---------------------------------------------------------------------------
  // Step 2: Connection Selection State
  // ---------------------------------------------------------------------------
  const [connections, setConnections] = useState<OrgConnection[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  // ---------------------------------------------------------------------------
  // Step 3: Repository Selection State
  // ---------------------------------------------------------------------------
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [reposError, setReposError] = useState<string | null>(null);
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [repoPage, setRepoPage] = useState(1);
  const [hasMoreRepos, setHasMoreRepos] = useState(false);
  const [allReposLoaded, setAllReposLoaded] = useState(false);

  // ---------------------------------------------------------------------------
  // Step 4: Project Details State
  // ---------------------------------------------------------------------------
  const [projectName, setProjectName] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Data Fetching Functions
  // ---------------------------------------------------------------------------

  const fetchOrgConnections = useCallback(
    async (orgId: string) => {
      setIsLoadingConnections(true);
      setConnectionsError(null);

      try {
        const response = await fetchWithAuth(
          `/admin/organizations/${orgId}/connections`
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to load connections');
        }

        const { data } = await response.json();

        // Filter to git/github connections only
        const gitConnections = data.connections.filter(
          (c: OrgConnection) =>
            c.provider_type === 'git' && c.provider_id === 'github'
        );

        setConnections(gitConnections);
      } catch (err) {
        setConnectionsError(
          err instanceof Error ? err.message : 'Failed to load connections'
        );
      } finally {
        setIsLoadingConnections(false);
      }
    },
    [fetchWithAuth]
  );

  const fetchRepos = useCallback(
    async (connectionId: string, page = 1, append = false) => {
      setIsLoadingRepos(true);
      setReposError(null);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          perPage: '30',
          sort: 'updated',
        });

        const response = await fetchWithAuth(
          `/admin/connections/${connectionId}/repos?${params}`
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to fetch repositories');
        }

        const { data } = await response.json();

        if (append) {
          setRepos((prev) => [...prev, ...data.repos]);
        } else {
          setRepos(data.repos);
        }
        setHasMoreRepos(data.pagination.hasMore);
        setRepoPage(page);
      } catch (err) {
        setReposError(
          err instanceof Error ? err.message : 'Failed to fetch repositories'
        );
      } finally {
        setIsLoadingRepos(false);
      }
    },
    [fetchWithAuth]
  );

  const loadAllRepos = useCallback(
    async (connectionId: string) => {
      if (allReposLoaded || isLoadingRepos) return;

      setIsLoadingRepos(true);
      let page = repoPage;
      let allRepos = [...repos];
      let hasMore = hasMoreRepos;

      try {
        while (hasMore) {
          page += 1;
          const params = new URLSearchParams({
            page: page.toString(),
            perPage: '100',
            sort: 'updated',
          });

          const response = await fetchWithAuth(
            `/admin/connections/${connectionId}/repos?${params}`
          );
          if (!response.ok) break;

          const { data } = await response.json();
          allRepos = [...allRepos, ...data.repos];
          hasMore = data.pagination.hasMore;
        }

        setRepos(allRepos);
        setAllReposLoaded(true);
        setHasMoreRepos(false);
      } catch {
        // Silently handle repo loading failure
      } finally {
        setIsLoadingRepos(false);
      }
    },
    [
      allReposLoaded,
      isLoadingRepos,
      repoPage,
      repos,
      hasMoreRepos,
      fetchWithAuth,
    ]
  );

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setCurrentStep(initialStep);
        setSelectedOrgId(preselectedOrganizationId || '');
        setConnections([]);
        setIsLoadingConnections(false);
        setConnectionsError(null);
        setSelectedConnectionId(null);
        setRepos([]);
        setIsLoadingRepos(false);
        setReposError(null);
        setRepoSearch('');
        setSelectedRepo(null);
        setRepoPage(1);
        setHasMoreRepos(false);
        setAllReposLoaded(false);
        setProjectName('');
        setProjectSlug('');
        setProjectDescription('');
        setIsCreating(false);
        setCreateError(null);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, initialStep, preselectedOrganizationId]);

  // Auto-fetch connections when modal opens with preselected org
  useEffect(() => {
    if (open && preselectedOrganizationId) {
      fetchOrgConnections(preselectedOrganizationId);
    }
  }, [open, preselectedOrganizationId, fetchOrgConnections]);

  // Pre-fill project details when repo is selected
  useEffect(() => {
    if (selectedRepo) {
      setProjectName(selectedRepo.name);
      setProjectSlug(generateSlug(selectedRepo.name));
      setProjectDescription(selectedRepo.description || '');
    }
  }, [selectedRepo]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (name: string) => {
    setProjectName(name);
    if (!projectSlug || projectSlug === generateSlug(projectName)) {
      setProjectSlug(generateSlug(name));
    }
  };

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    // Reset downstream selections
    setSelectedConnectionId(null);
    setSelectedRepo(null);
    setConnections([]);
    setRepos([]);
    setAllReposLoaded(false);
  };

  const handleLoadMore = () => {
    if (!isLoadingRepos && hasMoreRepos && selectedConnectionId) {
      fetchRepos(selectedConnectionId, repoPage + 1, true);
    }
  };

  const handleCreate = async () => {
    if (
      !selectedRepo ||
      !projectName ||
      !projectSlug ||
      !selectedOrgId ||
      !selectedConnectionId
    ) {
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await fetchWithAuth('/admin/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: projectName,
          slug: projectSlug,
          description: projectDescription || undefined,
          repositoryUrl: selectedRepo.url,
          organizationId: selectedOrgId,
          connectionId: selectedConnectionId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create project');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create project'
      );
    } finally {
      setIsCreating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const canProceedStep1 = !!selectedOrgId;
  const canProceedStep2 = !!selectedConnectionId;
  const canProceedStep3 = !!selectedRepo;
  const canProceedStep4 = projectName && projectSlug && selectedOrgId;

  const handleNext = () => {
    if (currentStep === 0 && canProceedStep1) {
      fetchOrgConnections(selectedOrgId);
      setCurrentStep(1);
    } else if (currentStep === 1 && canProceedStep2) {
      fetchRepos(selectedConnectionId!);
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedStep3) {
      setCurrentStep(3);
    } else if (currentStep === 3 && canProceedStep4) {
      handleCreate();
    }
  };

  const handleBack = () => {
    // Don't go back past initialStep (respects preselected org)
    if (currentStep > initialStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived Data
  // ---------------------------------------------------------------------------

  const selectedOrg = useMemo(
    () => organizations.find((org) => org.id === selectedOrgId),
    [organizations, selectedOrgId]
  );

  const filteredRepos = useMemo(() => {
    if (!repoSearch.trim()) return repos;
    const search = repoSearch.toLowerCase();
    return repos.filter(
      (repo) =>
        repo.name.toLowerCase().includes(search) ||
        repo.fullName.toLowerCase().includes(search) ||
        (repo.description?.toLowerCase().includes(search) ?? false)
    );
  }, [repos, repoSearch]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
          <DialogDescription>
            Set up a new project by connecting to your Git repository.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="lg">
            {/* Step Indicator */}
            <WizardStep
              steps={wizardSteps}
              activeStep={displayStepIndex}
              size="sm"
            />

            {/* Step Content */}
            <Stack className="min-h-[300px]">
              {/* Step 1: Organization Selection */}
              {currentStep === 0 && (
                <Stack gap="md">
                  <Stack gap="xs">
                    <Label required>Organization</Label>
                    <Select
                      value={selectedOrgId}
                      onValueChange={handleOrgChange}
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue placeholder="Select an organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Text color="muted" size="xs" className="mt-1">
                      The organization that will own this project
                    </Text>
                  </Stack>
                </Stack>
              )}

              {/* Step 2: Connection Selection */}
              {currentStep === 1 && (
                <Stack gap="md">
                  {isLoadingConnections ? (
                    <Center minHeight="8rem">
                      <Spinner size="md" variant="muted" />
                    </Center>
                  ) : connectionsError ? (
                    <Stack gap="sm" align="center" className="py-8">
                      <AlertCircle className="text-error h-8 w-8" />
                      <Text color="error">{connectionsError}</Text>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          selectedOrgId && fetchOrgConnections(selectedOrgId)
                        }
                      >
                        Retry
                      </Button>
                    </Stack>
                  ) : connections.length === 0 ? (
                    // Empty state - no connections
                    <Card className="bg-surface-2 p-6">
                      <Stack gap="md" align="center" className="text-center">
                        <Center className="bg-surface-3 h-16 w-16 rounded-full">
                          <Github className="text-foreground h-8 w-8" />
                        </Center>
                        <Stack gap="none">
                          <Text weight="medium" color="default">
                            No GitHub Connections
                          </Text>
                          <Text size="sm" color="muted" className="mt-1">
                            This organization doesn&apos;t have any GitHub
                            connections yet.
                          </Text>
                        </Stack>
                        <Button
                          onClick={() => {
                            if (selectedOrg) {
                              window.location.href = `/admin/organizations/${selectedOrg.slug}?tab=connections&addConnection=github`;
                            }
                          }}
                          className="gap-2"
                        >
                          <Github className="h-4 w-4" />
                          Add GitHub Connection
                        </Button>
                      </Stack>
                    </Card>
                  ) : (
                    // Connection list
                    <Stack gap="sm">
                      <Text weight="medium" color="default">
                        Select a GitHub connection
                      </Text>
                      <Stack gap="xs" className="max-h-[400px] overflow-y-auto">
                        {connections.map((connection) => {
                          const isSelected =
                            selectedConnectionId === connection.id;
                          const githubAccount =
                            connection.metadata.github_account;

                          return (
                            <Button
                              key={connection.id}
                              variant="outline"
                              type="button"
                              onClick={() =>
                                setSelectedConnectionId(connection.id)
                              }
                              className={`h-auto w-full justify-start rounded-lg border-2 p-4 text-left ${
                                isSelected ? 'border-primary bg-primary/5' : ''
                              } `}
                            >
                              <Inline justify="between" gap="sm">
                                <Inline gap="sm">
                                  <Center className="bg-surface-3 h-10 w-10 shrink-0 rounded-full">
                                    <Github className="h-5 w-5" />
                                  </Center>
                                  <Stack className="min-w-0" gap="none">
                                    <Inline gap="xs">
                                      <Text
                                        weight="medium"
                                        color="default"
                                        truncate
                                      >
                                        {connection.label}
                                      </Text>
                                      <ConnectionHealthBadge
                                        isAppInstallation={!!githubAccount}
                                        validationStatus={
                                          connection.validation_status
                                        }
                                      />
                                    </Inline>
                                    {githubAccount && (
                                      <Text
                                        size="sm"
                                        color="muted"
                                        className="truncate"
                                      >
                                        {githubAccount.type === 'Organization'
                                          ? ''
                                          : '@'}
                                        {githubAccount.login}
                                        {connection.metadata
                                          .repository_selection && (
                                          <Text
                                            as="span"
                                            size="xs"
                                            className="ml-1"
                                          >
                                            (
                                            {connection.metadata
                                              .repository_selection === 'all'
                                              ? 'all repos'
                                              : 'selected repos'}
                                            )
                                          </Text>
                                        )}
                                      </Text>
                                    )}
                                  </Stack>
                                </Inline>
                                {isSelected && (
                                  <Check className="text-primary h-5 w-5 shrink-0" />
                                )}
                              </Inline>
                            </Button>
                          );
                        })}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}

              {/* Step 3: Repository Selection */}
              {currentStep === 2 && (
                <Stack gap="md">
                  {/* Search */}
                  <Stack gap="xs">
                    <Input
                      type="text"
                      placeholder={
                        repos.length > 0
                          ? `Search ${repos.length} repositories...`
                          : 'Search repositories...'
                      }
                      value={repoSearch}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRepoSearch(value);
                        // Load all repos when user starts searching
                        if (value && !allReposLoaded && selectedConnectionId) {
                          loadAllRepos(selectedConnectionId);
                        }
                      }}
                      leftAddon={<Search className="h-4 w-4" />}
                    />
                    {isLoadingRepos && repoSearch && (
                      <Inline gap="xs" className="text-text-muted">
                        <Spinner size="xs" variant="muted" />
                        <Text size="xs" color="muted">
                          Loading all repositories to search...
                        </Text>
                      </Inline>
                    )}
                  </Stack>

                  {/* Repo List */}
                  {reposError ? (
                    <Stack gap="sm" align="center" className="py-8">
                      <AlertCircle className="text-error h-8 w-8" />
                      <Text color="error">{reposError}</Text>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          selectedConnectionId &&
                          fetchRepos(selectedConnectionId, 1)
                        }
                      >
                        Retry
                      </Button>
                    </Stack>
                  ) : (
                    <Stack gap="xs" className="max-h-[400px] overflow-y-auto">
                      {filteredRepos.length === 0 && !isLoadingRepos ? (
                        <Center className="py-8">
                          <Text color="muted">
                            {repoSearch
                              ? 'No repositories match your search'
                              : 'No repositories found'}
                          </Text>
                        </Center>
                      ) : (
                        <>
                          {filteredRepos.map((repo) => (
                            <Button
                              key={repo.id}
                              variant="outline"
                              type="button"
                              onClick={() => setSelectedRepo(repo)}
                              className={`h-auto w-full justify-start rounded-lg border-2 p-3 text-left ${
                                selectedRepo?.id === repo.id
                                  ? 'border-primary bg-primary/5'
                                  : ''
                              } `}
                            >
                              <Inline align="start" justify="between" gap="sm">
                                <Stack className="min-w-0 flex-1" gap="none">
                                  <Inline gap="xs">
                                    <Text
                                      weight="medium"
                                      color="default"
                                      truncate
                                    >
                                      {repo.name}
                                    </Text>
                                    {repo.isPrivate ? (
                                      <Lock className="text-text-muted h-3 w-3" />
                                    ) : (
                                      <Globe className="text-text-muted h-3 w-3" />
                                    )}
                                  </Inline>
                                  <Text
                                    size="xs"
                                    color="muted"
                                    className="truncate"
                                  >
                                    {repo.fullName}
                                  </Text>
                                  {repo.description && (
                                    <Text
                                      size="sm"
                                      color="secondary"
                                      className="mt-1 line-clamp-2"
                                    >
                                      {repo.description}
                                    </Text>
                                  )}
                                  <Inline gap="sm" className="mt-2">
                                    {repo.language && (
                                      <Text size="xs" color="muted">
                                        {repo.language}
                                      </Text>
                                    )}
                                    {repo.stars > 0 && (
                                      <Inline gap="xs">
                                        <Star className="text-text-muted h-3 w-3" />
                                        <Text size="xs" color="muted">
                                          {repo.stars}
                                        </Text>
                                      </Inline>
                                    )}
                                  </Inline>
                                </Stack>
                                {selectedRepo?.id === repo.id && (
                                  <Check className="text-primary h-5 w-5 shrink-0" />
                                )}
                              </Inline>
                            </Button>
                          ))}

                          {isLoadingRepos && (
                            <Stack gap="xs">
                              {[1, 2, 3].map((i) => (
                                <Skeleton key={i} className="h-24 w-full" />
                              ))}
                            </Stack>
                          )}

                          {hasMoreRepos && !isLoadingRepos && (
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={handleLoadMore}
                            >
                              Load More
                            </Button>
                          )}
                        </>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}

              {/* Step 4: Project Details */}
              {currentStep === 3 && (
                <Stack gap="md">
                  {createError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{createError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Selected Repo Summary */}
                  {selectedRepo && (
                    <Card className="bg-surface-2 p-3">
                      <Inline gap="sm">
                        <Github className="text-text-muted h-5 w-5" />
                        <Stack gap="none">
                          <Text weight="medium" color="default">
                            {selectedRepo.fullName}
                          </Text>
                          <Text size="xs" color="muted">
                            {selectedRepo.url}
                          </Text>
                        </Stack>
                      </Inline>
                    </Card>
                  )}

                  {/* Organization (read-only) */}
                  {selectedOrg && (
                    <Stack gap="xs">
                      <Label>Organization</Label>
                      <Card className="bg-surface-2 mt-1 px-3 py-2">
                        <Text color="default">{selectedOrg.name}</Text>
                      </Card>
                    </Stack>
                  )}

                  {/* Project Name */}
                  <Stack gap="xs">
                    <Label required>Project Name</Label>
                    <Input
                      type="text"
                      placeholder="My Project"
                      value={projectName}
                      onChange={(e) => handleNameChange(e.target.value)}
                      className="mt-1"
                    />
                  </Stack>

                  {/* Slug */}
                  <Stack gap="xs">
                    <Label required>Slug</Label>
                    <Input
                      type="text"
                      placeholder="my-project"
                      value={projectSlug}
                      onChange={(e) => setProjectSlug(e.target.value)}
                      className="mt-1"
                    />
                    <Text color="muted" size="xs" className="mt-1">
                      URL-friendly identifier. Only lowercase letters, numbers,
                      and hyphens.
                    </Text>
                  </Stack>

                  {/* Description */}
                  <Stack gap="xs">
                    <Label>Description</Label>
                    <TextArea
                      placeholder="A brief description of the project..."
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      className="mt-1"
                      rows={3}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Inline className="w-full" justify="between">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Inline gap="xs">
              {currentStep > initialStep && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={
                  (currentStep === 0 && !canProceedStep1) ||
                  (currentStep === 1 && !canProceedStep2) ||
                  (currentStep === 2 && !canProceedStep3) ||
                  (currentStep === 3 && (!canProceedStep4 || isCreating))
                }
                loading={isCreating}
              >
                {currentStep === 3 ? 'Create Project' : 'Next'}
              </Button>
            </Inline>
          </Inline>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
